const BLOG_IMAGES = [
    '/images/blog1.jpeg',
    '/images/blog2.jpeg',
    '/images/blog3.jpeg',
    '/images/blog4.jpeg',
    '/images/blog5.jpeg',
    '/images/blog6.jpeg',
    '/images/blog7.jpeg',
    '/images/blog8.jpeg',
    '/images/blog9.jpeg',
];

const ARTICLE_TEMPLATES = [
    {
        category: 'Market Analysis',
        tags: ['Bitcoin', 'Spot', 'Volume'],
        symbols: ['BTCUSDT'],
        readMinutes: 4,
        build: ({ primary, sentiment }) => ({
            title: `Bitcoin ${sentiment.verb} ${primary.priceLabel} as traders watch 24h volume`,
            excerpt: `BTC is ${sentiment.adjective} on the day, trading near ${primary.priceLabel} with a ${primary.changeLabel} move. Here is what the tape suggests for spot and bot positioning.`,
            paragraphs: [
                `Bitcoin last traded around ${primary.priceLabel}, marking a ${primary.changeLabel} change over the past 24 hours. ${sentiment.lead} When daily volume runs ${primary.volume > 25000 ? 'above' : 'near'} typical levels, breakouts tend to hold better — but only if follow-through appears on the next sessions.`,
                `For NeuraTrader users, this environment favors ${sentiment.botBias}. Algo bots can gate entries behind volume and trend filters; scheduled bots can keep DCA passive without chasing every wick.`,
                `Key levels to watch: if price rejects the current zone, grid bots should tighten ranges or pause until volatility compresses. Always size for the worst-case wick, not the best-case trend.`,
            ],
        }),
    },
    {
        category: 'Ethereum',
        tags: ['Ethereum', 'Layer 1', 'DeFi'],
        symbols: ['ETHUSDT', 'BTCUSDT'],
        readMinutes: 5,
        build: ({ primary, secondary, sentiment }) => ({
            title: `Ethereum ${sentiment.verb} while ETH/BTC ratio ${secondary ? (secondary.changePercent >= 0 ? 'firms' : 'softens') : 'stays mixed'}`,
            excerpt: `ETH at ${primary.priceLabel} (${primary.changeLabel}) — we break down what today's move means for DeFi liquidity and automated entries.`,
            paragraphs: [
                `Ethereum is ${sentiment.adjective} at ${primary.priceLabel}, ${primary.changeLabel} over 24h. ${secondary ? `Against Bitcoin, ETH's relative performance ${secondary.changePercent >= primary.changePercent ? 'has been resilient' : 'has lagged'}, which often signals whether alt liquidity is rotating or consolidating.` : 'Cross-asset context remains important when sizing alt exposure.'}`,
                `DeFi-heavy portfolios should treat sharp ETH moves as a volatility flag. Wider grid spacing and smaller order templates reduce the risk of getting filled into a one-way candle.`,
                `Scheduled accumulation on ETH works best with fixed intervals and hard wallet caps. Pair time-based buys with a max drawdown monitor so automation pauses during abnormal sell pressure.`,
            ],
        }),
    },
    {
        category: 'Trading Strategy',
        tags: ['Grid Bot', 'Range Markets', 'Automation'],
        symbols: ['BTCUSDT'],
        readMinutes: 4,
        build: ({ primary, sentiment }) => ({
            title: sentiment.isRangeDay
                ? `Range conditions favor grid bots as BTC holds ${primary.priceLabel}`
                : `Trend day alert: rethink grid spacing with BTC ${primary.changeLabel}`,
            excerpt: `Today's ${primary.changeLabel} session shapes how grid and algo bots should be configured on major pairs.`,
            paragraphs: [
                `With Bitcoin ${sentiment.adjective} and a ${primary.changeLabel} daily change, ${sentiment.isRangeDay ? 'price action looks more rotational than directional — a classic grid environment when bounds are set inside recent support and resistance.' : 'momentum is dominating — grids with tight spacing risk being run over unless bounds are widened materially.'}`,
                `Practical grid checklist: define upper and lower limits from the last 7–14 days, use limit orders only, and keep per-level size small enough to survive a 2–3% adverse move.`,
                `If conditions shift from range to trend, pause the grid and switch to an algo bot with trend filters (moving averages, RSI with context) rather than forcing the same parameters.`,
            ],
        }),
    },
    {
        category: 'Altcoins',
        tags: ['BNB', 'Liquidity', 'Spot'],
        symbols: ['BNBUSDT', 'BTCUSDT'],
        readMinutes: 4,
        build: ({ primary, sentiment }) => ({
            title: `BNB ${sentiment.verb} to ${primary.priceLabel} — liquidity leaders in focus`,
            excerpt: `Exchange-token strength often tracks broader risk appetite. Today's BNB move offers a read on alt participation.`,
            paragraphs: [
                `BNB trades near ${primary.priceLabel}, ${primary.changeLabel} in 24h. ${sentiment.lead} Liquid large-cap alts tend to lead rotations before mid-caps follow.`,
                `Bot traders should prefer pairs with tight spreads and deep books. Thin alt markets amplify slippage on market orders — use limits and smaller clips when automating.`,
                `When BNB and BTC diverge sharply, review correlation assumptions in multi-bot portfolios. Uncorrelated drawdowns can stack faster than expected.`,
            ],
        }),
    },
    {
        category: 'Futures',
        tags: ['Futures', 'Risk', 'Leverage'],
        symbols: ['BTCUSDT'],
        readMinutes: 5,
        build: ({ primary, sentiment }) => ({
            title: `Futures traders eye ${primary.changeLabel} BTC move near ${primary.priceLabel}`,
            excerpt: `Leverage amplifies today's tape. A disciplined approach to sizing and exits matters more than direction calls.`,
            paragraphs: [
                `Bitcoin's ${primary.changeLabel} session at ${primary.priceLabel} keeps futures desks sensitive to liquidation clusters and funding shifts. ${sentiment.lead}`,
                `If you run futures bots, start with low leverage, enable reduce-only on exits, and never mirror spot sizing. A 1% spot risk budget is not a 1% futures budget.`,
                `Volatility spikes are when automation errors cost the most — enable logging, cap open orders, and use wallet allocation limits on every new strategy rollout.`,
            ],
        }),
    },
    {
        category: 'DCA & Scheduling',
        tags: ['Scheduled Bot', 'DCA', 'Long-term'],
        symbols: ['BTCUSDT', 'ETHUSDT'],
        readMinutes: 3,
        build: ({ primary, secondary, sentiment }) => ({
            title: `DCA discipline in a ${sentiment.adjective} tape: BTC & ETH snapshot`,
            excerpt: `Time-based buying removes timing stress — but schedules still need guardrails when markets move fast.`,
            paragraphs: [
                `Today's snapshot: BTC ${primary.priceLabel} (${primary.changeLabel})${secondary ? `, ETH ${secondary.priceLabel} (${secondary.changeLabel})` : ''}. Scheduled bots shine when you commit to a calendar rather than a price prediction.`,
                `Fixed weekly or bi-weekly entries smooth acquisition cost over months. Keep each buy small relative to total portfolio so a extended drawdown does not force manual overrides.`,
                `Combine DCA with a simple monitor: pause scheduled buys if daily change exceeds a threshold you define. Automation should adapt, not blindly fire into chaos.`,
            ],
        }),
    },
    {
        category: 'Market Structure',
        tags: ['Volume', 'Breakouts', 'Algo Bot'],
        symbols: ['BTCUSDT'],
        readMinutes: 4,
        build: ({ primary, sentiment }) => ({
            title: `Volume ${primary.volume > 25000 ? 'confirms' : 'cools on'} today's BTC ${sentiment.moveType}`,
            excerpt: `Price without volume is a warning sign. We connect today's BTC tape to algo entry rules.`,
            paragraphs: [
                `Bitcoin at ${primary.priceLabel} (${primary.changeLabel}) arrives with ${primary.volume > 25000 ? 'elevated' : 'moderate'} reported 24h volume. ${sentiment.lead}`,
                `Algo strategies should require volume confirmation for breakout entries — e.g. only buy when price clears a level and volume is above its recent average.`,
                `False breaks are expensive for bots using market orders. Prefer limit entries at retests and keep position caps consistent across monitors.`,
            ],
        }),
    },
    {
        category: 'Risk Management',
        tags: ['Portfolio', 'Sizing', 'Bots'],
        symbols: ['BTCUSDT', 'ETHUSDT'],
        readMinutes: 4,
        build: ({ primary, secondary, sentiment }) => ({
            title: `Portfolio heat check: majors ${sentiment.adjective} into the close`,
            excerpt: `Before adding bots or size, run a quick risk pass on correlated exposure across BTC, ETH, and alts.`,
            paragraphs: [
                `BTC ${primary.priceLabel} (${primary.changeLabel})${secondary ? ` and ETH ${secondary.priceLabel} (${secondary.changeLabel})` : ''} set the tone for cross-market beta. ${sentiment.lead}`,
                `Cap total automated exposure per asset class. Running grid, algo, and scheduled bots on the same pair without aggregate limits can silently over-leverage.`,
                `Weekly review: fill rates, error logs, realized vs expected slippage. Small parameter tweaks beat ripping out working strategies after one red day.`,
            ],
        }),
    },
    {
        category: 'Solana Ecosystem',
        tags: ['Solana', 'Altcoins', 'Volatility'],
        symbols: ['SOLUSDT', 'BTCUSDT'],
        readMinutes: 4,
        build: ({ primary, sentiment }) => ({
            title: `SOL ${sentiment.verb} toward ${primary.priceLabel} as alt beta ${sentiment.altBeta}`,
            excerpt: `High-beta L1 moves draw bot traders into faster timeframes — with higher tail risk.`,
            paragraphs: [
                `Solana trades near ${primary.priceLabel}, ${primary.changeLabel} over 24h. ${sentiment.lead} Alts can outperform in risk-on stretches but retrace harder when BTC stalls.`,
                `Grid bots on SOL need wider bands than on BTC — intraday swings are larger in percentage terms. Reduce per-grid notional accordingly.`,
                `If you use algo entries on SOL, add cooldown periods after fills to avoid churn in whip-saw conditions.`,
            ],
        }),
    },
    {
        category: 'Macro & Sentiment',
        tags: ['Sentiment', 'Bitcoin', 'Outlook'],
        symbols: ['BTCUSDT'],
        readMinutes: 5,
        build: ({ primary, sentiment }) => ({
            title: sentiment.headline,
            excerpt: `A concise read on today's Bitcoin price action at ${primary.priceLabel} and what it implies for systematic traders.`,
            paragraphs: [
                `Bitcoin is ${primary.changeLabel} at ${primary.priceLabel}. ${sentiment.macroLead}`,
                `Systematic traders care less about single headlines and more about repeatable edges: execution quality, fee drag, and rule adherence across hundreds of trades.`,
                `Use today's tape as context, not prophecy. Bots win on process — defined entries, defined size, defined stops, and logs that make post-mortems honest.`,
            ],
        }),
    },
    {
        category: 'Bot Operations',
        tags: ['Automation', 'Monitoring', 'Best Practices'],
        symbols: ['ETHUSDT'],
        readMinutes: 3,
        build: ({ primary, sentiment }) => ({
            title: `Running bots in ${sentiment.adjective} ETH conditions`,
            excerpt: `ETH at ${primary.priceLabel} (${primary.changeLabel}) — operational tips for live automations.`,
            paragraphs: [
                `Ethereum's ${primary.changeLabel} move keeps gas and volatility considerations relevant for active strategies. ${sentiment.lead}`,
                `First 48 hours on any new bot: enable verbose logging, halve default size, and verify symbol, quantity precision, and min notional on the exchange.`,
                `Set Telegram or email alerts on order errors. Silent failures are worse than loud ones — you can only fix what you see.`,
            ],
        }),
    },
    {
        category: 'XRP & Payments',
        tags: ['XRP', 'Liquidity', 'Spot'],
        symbols: ['XRPUSDT', 'BTCUSDT'],
        readMinutes: 4,
        build: ({ primary, sentiment }) => ({
            title: `XRP ${sentiment.verb} near ${primary.priceLabel} — rotation watch`,
            excerpt: `Payment-focused tokens sometimes decouple from BTC. Today's XRP tape is worth a cross-check against your bot correlations.`,
            paragraphs: [
                `XRP last at ${primary.priceLabel}, ${primary.changeLabel} in 24h. ${sentiment.lead}`,
                `When a single alt bot runs against a quiet BTC tape, correlation assumptions break down. Track portfolio beta weekly, not only PnL.`,
                `Prefer limit grids on XRP during headline-driven sessions; market orders can slip when social volume spikes.`,
            ],
        }),
    },
    {
        category: 'Cardano',
        tags: ['ADA', 'Altcoins', 'Grid'],
        symbols: ['ADAUSDT'],
        readMinutes: 3,
        build: ({ primary, sentiment }) => ({
            title: `ADA ${sentiment.verb} at ${primary.priceLabel}: grid-friendly or trend day?`,
            excerpt: `Mid-cap alts reward patient automation parameters. We map today's ADA move to practical bot settings.`,
            paragraphs: [
                `Cardano at ${primary.priceLabel} (${primary.changeLabel}) — ${sentiment.isRangeDay ? 'movement looks contained enough for careful grid deployment inside a defined range.' : 'directional energy suggests grids should stay off until price stabilizes.'}`,
                `Use fewer grid levels on ADA than on BTC; each level should be meaningful in notional terms but small as a fraction of wallet balance.`,
                `Backtest grid bounds against the last 30 days before going live. Historical range width informs spacing better than intuition.`,
            ],
        }),
    },
    {
        category: 'Dogecoin',
        tags: ['DOGE', 'Meme', 'Volatility'],
        symbols: ['DOGEUSDT', 'BTCUSDT'],
        readMinutes: 3,
        build: ({ primary, sentiment }) => ({
            title: `DOGE ${sentiment.verb} ${primary.changeLabel} — high-noise pair playbook`,
            excerpt: `Meme-led pairs punish oversized bots. Today's DOGE snapshot comes with sizing reminders.`,
            paragraphs: [
                `Dogecoin at ${primary.priceLabel}, ${primary.changeLabel} over 24h. ${sentiment.lead} Social-driven spikes fade as quickly as they appear.`,
                `Treat DOGE bots as satellite exposure: low wallet share, strict caps, and willingness to pause when daily range doubles overnight.`,
                `Algo entries should include time-based cooldowns and maximum trades per day to prevent overtrading in noisy tape.`,
            ],
        }),
    },
    {
        category: 'Weekly Outlook',
        tags: ['Outlook', 'Bitcoin', 'Planning'],
        symbols: ['BTCUSDT', 'ETHUSDT'],
        readMinutes: 5,
        build: ({ primary, secondary, sentiment }) => ({
            title: `Week ahead: positioning after BTC ${primary.changeLabel} / ETH ${secondary ? secondary.changeLabel : 'mixed'}`,
            excerpt: `Use the daily close to calibrate bot parameters for the coming sessions — not to predict every wick.`,
            paragraphs: [
                `Snapshot entering the week: BTC ${primary.priceLabel}, ETH ${secondary ? secondary.priceLabel : 'unchanged'}. ${sentiment.macroLead}`,
                `Reconcile active bots with this backdrop: tighten risk if multiple strategies lean the same direction, diversify if everything is correlated long.`,
                `Plan maintenance windows for parameter updates outside peak liquidity hours. Fewer surprise fills during thin books.`,
            ],
        }),
    },
];

function getDateKey(date = new Date()) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

function hashString(value) {
    let hash = 0;
    for (let i = 0; i < value.length; i += 1) {
        hash = ((hash << 5) - hash + value.charCodeAt(i)) >>> 0;
    }
    return hash;
}

function formatPrice(value) {
    if (value >= 1000) {
        return `$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
    }
    if (value >= 1) {
        return `$${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
    }
    return `$${value.toLocaleString(undefined, { maximumFractionDigits: 4 })}`;
}

function formatChange(changePercent) {
    const sign = changePercent >= 0 ? '+' : '';
    return `${sign}${changePercent.toFixed(2)}%`;
}

function buildSentiment(changePercent) {
    const abs = Math.abs(changePercent);
    const isRangeDay = abs < 1.5;

    if (changePercent >= 2) {
        return {
            adjective: 'firmly higher',
            verb: 'rallies',
            lead: 'Momentum buyers have controlled the tape, with dips being bought relatively quickly.',
            botBias: 'trend-following filters on algo bots, with trailing exits rather than fixed take-profits',
            moveType: 'breakout',
            altBeta: 'expands',
            headline: `Risk-on tone builds as Bitcoin pushes ${formatChange(changePercent)}`,
            macroLead: 'Risk appetite has improved, but chasing extended moves without size discipline remains the main failure mode for retail automation.',
            isRangeDay,
        };
    }
    if (changePercent >= 0.5) {
        return {
            adjective: 'modestly higher',
            verb: 'edges up',
            lead: 'Buyers have a slight edge, though the move is not yet a full trend day.',
            botBias: 'balanced grids with modest bullish bias or light scheduled accumulation',
            moveType: 'grind higher',
            altBeta: 'stays constructive',
            headline: `Cautious optimism as Bitcoin posts a ${formatChange(changePercent)} session`,
            macroLead: 'Markets are constructive but not euphoric — a regime where process-driven bots often outperform discretionary overtrading.',
            isRangeDay,
        };
    }
    if (changePercent > -0.5) {
        return {
            adjective: 'mixed',
            verb: 'trades flat',
            lead: 'Neither side has established clear control; rotation and mean reversion dominate.',
            botBias: 'range-bound grids with neutral bias and tight risk caps',
            moveType: 'consolidation',
            altBeta: 'stays mixed',
            headline: `Bitcoin consolidates near unchanged on the day`,
            macroLead: 'Flat sessions are ideal for testing automation parameters without the noise of a macro shock.',
            isRangeDay,
        };
    }
    if (changePercent > -2) {
        return {
            adjective: 'under pressure',
            verb: 'slips',
            lead: 'Sellers have edged ahead; rallies are being sold into resistance.',
            botBias: 'reduced size, wider stops, and paused grid expansion until support holds',
            moveType: 'pullback',
            altBeta: 'compresses',
            headline: `Defensive tone returns with Bitcoin ${formatChange(changePercent)}`,
            macroLead: 'Pullbacks test whether automation rules include downside scenarios — not only bullish templates.',
            isRangeDay,
        };
    }
    return {
        adjective: 'sharply lower',
        verb: 'sells off',
        lead: 'Risk-off flow dominates; liquidity can thin during fast moves.',
        botBias: 'minimal new exposure, strict wallet caps, and paused grids until volatility settles',
        moveType: 'sell-off',
        altBeta: 'contracts hard',
        headline: `Risk-off session: Bitcoin drops ${formatChange(changePercent)}`,
        macroLead: 'High-volatility down days are when bot misconfiguration surfaces. Review logs before adding size.',
        isRangeDay,
    };
}

function mapTickerToSnapshot(data, symbol) {
    const price = parseFloat(data.lastPrice);
    const changePercent = parseFloat(data.priceChangePercent);
    const volume = parseFloat(data.volume);

    return {
        symbol: data.symbol || symbol,
        displaySymbol: (data.symbol || symbol).replace('USDT', ''),
        price,
        priceLabel: formatPrice(price),
        changePercent: Number.isFinite(changePercent) ? changePercent : 0,
        changeLabel: formatChange(Number.isFinite(changePercent) ? changePercent : 0),
        volume: Number.isFinite(volume) ? volume : 0,
    };
}

async function fetchTickerSnapshot(symbol) {
    const { getMarketTicker } = await import('../services/MarketService');
    const data = await getMarketTicker(symbol);
    return mapTickerToSnapshot(data, symbol);
}

async function fetchTickerSnapshots(symbols) {
    const uniqueSymbols = [...new Set(symbols.filter(Boolean))];
    if (!uniqueSymbols.length) return [];

    const { getMarketTickers } = await import('../services/MarketService');
    const { tickers } = await getMarketTickers(uniqueSymbols);
    const tickerBySymbol = new Map(
        (tickers || []).map((ticker) => [ticker.symbol, ticker]),
    );

    return uniqueSymbols
        .map((symbol) => {
            const ticker = tickerBySymbol.get(symbol);
            return ticker ? mapTickerToSnapshot(ticker, symbol) : null;
        })
        .filter(Boolean);
}

async function resolvePrimaryMarket(templateSymbols, markets) {
    const primary = markets.find((market) => market.symbol === templateSymbols[0])
        || markets[0];

    if (primary && Number.isFinite(primary.changePercent) && primary.price > 0) {
        return primary;
    }

    try {
        return await fetchTickerSnapshot(templateSymbols[0] || 'BTCUSDT');
    } catch {
        try {
            return await fetchTickerSnapshot('BTCUSDT');
        } catch {
            return null;
        }
    }
}

function enrichMarket(market) {
    return {
        ...market,
        direction: market.changePercent >= 0 ? 'up' : 'down',
    };
}

export function getDailyBlogPost(date = new Date()) {
    const dateKey = getDateKey(date);
    const templateIndex = hashString(`${dateKey}-blog`) % ARTICLE_TEMPLATES.length;
    const imageIndex = hashString(`${dateKey}-img`) % BLOG_IMAGES.length;
    const template = ARTICLE_TEMPLATES[templateIndex];

    const placeholderMarket = {
        symbol: template.symbols[0],
        displaySymbol: template.symbols[0].replace('USDT', ''),
        price: 0,
        priceLabel: '—',
        changePercent: 0,
        changeLabel: '0.00%',
        volume: 0,
    };

    const sentiment = buildSentiment(0);
    const content = template.build({
        primary: placeholderMarket,
        secondary: null,
        sentiment,
    });

    return {
        id: `blog-${dateKey}`,
        dateKey,
        dayLabel: date.toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric',
        }),
        category: template.category,
        tags: template.tags,
        readMinutes: template.readMinutes,
        image: BLOG_IMAGES[imageIndex],
        title: content.title,
        excerpt: content.excerpt,
        paragraphs: content.paragraphs,
        markets: [],
        author: 'NeuraTrader Editorial',
    };
}

export async function getEnrichedDailyBlogPost(date = new Date()) {
    const dateKey = getDateKey(date);
    const templateIndex = hashString(`${dateKey}-blog`) % ARTICLE_TEMPLATES.length;
    const imageIndex = hashString(`${dateKey}-img`) % BLOG_IMAGES.length;
    const template = ARTICLE_TEMPLATES[templateIndex];

    let markets = [];
    try {
        markets = await fetchTickerSnapshots(template.symbols);
    } catch {
        markets = [];
    }

    const primaryMarket = await resolvePrimaryMarket(template.symbols, markets);
    const primary = primaryMarket || {
        symbol: template.symbols[0],
        displaySymbol: template.symbols[0].replace('USDT', ''),
        price: 0,
        priceLabel: '—',
        changePercent: 0,
        changeLabel: '0.00%',
        volume: 0,
    };

    const secondary = markets.find((market) => market.symbol === template.symbols[1])
        || markets[1]
        || null;

    if (!markets.length && primaryMarket) {
        markets = [primaryMarket, ...(secondary ? [secondary] : [])];
    }

    const sentiment = buildSentiment(primary.changePercent);
    const content = template.build({ primary, secondary, sentiment });

    return {
        id: `blog-${dateKey}`,
        dateKey,
        dayLabel: date.toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric',
        }),
        category: template.category,
        tags: template.tags,
        readMinutes: template.readMinutes,
        image: BLOG_IMAGES[imageIndex],
        title: content.title,
        excerpt: content.excerpt,
        paragraphs: content.paragraphs,
        markets: markets.map(enrichMarket),
        author: 'NeuraTrader Editorial',
    };
}

export function getBlogPostPoolSize() {
    return ARTICLE_TEMPLATES.length;
}